import type { Color, GradientStop } from '@flobi/artwork-core';

/**
 * Gradient stops as the bar edits them. Pure, so the arithmetic is tested apart from the
 * pointer: every change answers with the stops in order AND where the stop being worked
 * on ended up, because sorting moves it and the bar has to keep hold of it.
 */

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

/** The colour the gradient has at a point, between the stops either side of it. */
export function colorAt(stops: readonly GradientStop[], at: number): Color {
  const sorted = [...stops].sort((a, b) => a.offset - b.offset);
  const t = clamp01(at);
  if (sorted.length === 0) return { r: 0, g: 0, b: 0, a: 1 };
  if (t <= sorted[0]!.offset) return { ...sorted[0]!.color };
  const last = sorted[sorted.length - 1]!;
  if (t >= last.offset) return { ...last.color };
  for (let i = 0; i + 1 < sorted.length; i++) {
    const a = sorted[i]!;
    const b = sorted[i + 1]!;
    if (t >= a.offset && t <= b.offset) {
      const k = b.offset === a.offset ? 0 : (t - a.offset) / (b.offset - a.offset);
      const mix = (x: number, y: number) => x + (y - x) * k;
      return { r: mix(a.color.r, b.color.r), g: mix(a.color.g, b.color.g), b: mix(a.color.b, b.color.b), a: mix(a.color.a ?? 1, b.color.a ?? 1) };
    }
  }
  return { ...last.color };
}

function settle(stops: GradientStop[], held: GradientStop): { stops: GradientStop[]; index: number } {
  const sorted = [...stops].sort((a, b) => a.offset - b.offset);
  return { stops: sorted, index: sorted.indexOf(held) };
}

/** A stop added where the bar was pressed, in the colour the gradient already has there — so adding changes nothing until it is moved. */
export function addStop(stops: readonly GradientStop[], at: number): { stops: GradientStop[]; index: number } {
  const made = { offset: clamp01(at), color: colorAt(stops, at) };
  return settle([...stops, made], made);
}

export function moveStop(stops: readonly GradientStop[], index: number, to: number): { stops: GradientStop[]; index: number } {
  const moved = { ...stops[index]!, offset: clamp01(to) };
  return settle(stops.map((stop, at) => (at === index ? moved : stop)), moved);
}

export function setStopColor(stops: readonly GradientStop[], index: number, color: Color): GradientStop[] {
  return stops.map((stop, at) => (at === index ? { ...stop, color } : stop));
}

/** Two stops are the fewest a gradient has, so the last pair stays. */
export function removeStop(stops: readonly GradientStop[], index: number): { stops: GradientStop[]; index: number } {
  if (stops.length <= 2) return { stops: [...stops], index };
  const next = stops.filter((_, at) => at !== index);
  return { stops: next, index: Math.min(index, next.length - 1) };
}

/** The same colours running the other way. */
export function reverseStops(stops: readonly GradientStop[]): GradientStop[] {
  return [...stops].map((stop) => ({ ...stop, offset: 1 - stop.offset })).sort((a, b) => a.offset - b.offset);
}
